import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Search,
    Car,
    Bike,
    X,
    Phone,
    Building2,
    ShieldCheck,
    User,
    Camera,
    AlertTriangle,
    BellRing,
    Check,
    RefreshCw,
    Trash2,
    MapPin,
    Radio
} from "lucide-react";
import VisitorPhotoModal from "@/components/VisitorPhotoModal";

export default function VehicleGateLookup({ open, onClose, seniorMode = true, lang = "en" }) {
    const isHi = lang === "hi";
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [activePhotoVisitor, setActivePhotoVisitor] = useState(null);

    // Unauthorized Vehicle Report Modal state
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportVehicleNo, setReportVehicleNo] = useState("");
    const [reportTower, setReportTower] = useState("C");
    const [reportSpot, setReportSpot] = useState("");
    const [reportNotes, setReportNotes] = useState("");
    const [reportPhotoFile, setReportPhotoFile] = useState(null);
    const [reportPhotoPreview, setReportPhotoPreview] = useState(null);
    const [reporting, setReporting] = useState(false);
    const [resolvingId, setResolvingId] = useState(null);
    const [activeViolations, setActiveViolations] = useState([]);
    const [activeTab, setActiveTab] = useState("search"); // "search" | "violations"

    const loadViolations = useCallback(async () => {
        try {
            const res = await api.get("/guard/vehicles/unauthorized?status=active");
            setActiveViolations(res.data || []);
        } catch {
            setActiveViolations([]);
        }
    }, []);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (open) {
            loadViolations();
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prevOverflow;
            };
        } else {
            setQuery("");
            setResults([]);
            setSearched(false);
            setShowReportModal(false);
            setActiveTab("search");
        }
    }, [open, loadViolations]);

    const performLookup = useCallback(async (qStr) => {
        const q = (qStr || "").trim();
        if (q.length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        setLoading(true);
        try {
            const res = await api.get(`/guard/vehicles/lookup?q=${encodeURIComponent(q)}`);
            setResults(res.data || []);
            setSearched(true);
        } catch {
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        const timer = setTimeout(() => {
            performLookup(q);
        }, 200);
        return () => clearTimeout(timer);
    }, [query, performLookup]);

    const handleOpenReport = (initialNo = "") => {
        setReportVehicleNo(initialNo || query || "");
        setReportTower("C");
        setReportSpot("");
        setReportNotes("");
        setReportPhotoFile(null);
        setReportPhotoPreview(null);
        setShowReportModal(true);
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setReportPhotoFile(file);
            setReportPhotoPreview(URL.createObjectURL(file));
        } else {
            setReportPhotoFile(null);
            setReportPhotoPreview(null);
        }
    };

    const handleClearPhoto = () => {
        setReportPhotoFile(null);
        setReportPhotoPreview(null);
    };

    const handleSubmitReport = async (e) => {
        e.preventDefault();
        const cleanNo = reportVehicleNo.trim().toUpperCase();
        if (!cleanNo) {
            toast.error(isHi ? "गाड़ी नंबर दर्ज करें" : "Please enter vehicle plate number");
            return;
        }

        setReporting(true);
        try {
            const formData = new FormData();
            formData.append("vehicle_no", cleanNo);
            formData.append("tower", reportTower);
            if (reportSpot.trim()) formData.append("parking_spot", reportSpot.trim());
            if (reportNotes.trim()) formData.append("notes", reportNotes.trim());
            if (reportPhotoFile) formData.append("photo", reportPhotoFile);

            await api.post("/guard/vehicles/unauthorized", formData);
            toast.success(
                isHi
                    ? `✓ विंग ${reportTower} के सभी निवासियों को फोटो सहित अलर्ट भेजा गया!`
                    : `✓ Unauthorized alert & photo broadcasted to Wing ${reportTower} residents!`
            );
            setShowReportModal(false);
            setQuery(cleanNo);
            await loadViolations();
            await performLookup(cleanNo);
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "अलर्ट भेजने में विफल" : "Failed to broadcast alert"));
        } finally {
            setReporting(false);
        }
    };

    const handleResolveAlert = async (alertId, vehicleNo) => {
        setResolvingId(alertId);
        try {
            await api.post(`/guard/vehicles/unauthorized/${alertId}/resolve`);
            toast.success(isHi ? `✓ गाड़ी ${vehicleNo} की समस्या सुलझा दी गई` : `✓ Violation resolved for ${vehicleNo}`);
            await loadViolations();
            if (query.trim().length >= 2) {
                await performLookup(query);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "हल करने में विफल" : "Failed to resolve alert"));
        } finally {
            setResolvingId(null);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex flex-col justify-end sm:justify-center items-center bg-black/60 p-0 sm:p-4 backdrop-blur-xs animate-fade-in"
            onClick={onClose}
        >
            <div
                className={`bg-white rounded-t-3xl sm:rounded-3xl border border-nest-line w-full max-w-xl ${
                    seniorMode ? "p-4 sm:p-6" : "p-4 sm:p-6"
                } max-h-[92dvh] sm:max-h-[88vh] flex flex-col shadow-2xl relative animate-fade-up sm:animate-scale-up`}
                onClick={(e) => e.stopPropagation()}
                data-testid="vehicle-lookup-modal"
            >
                {/* Modal Header */}
                <div className="flex items-center justify-between pb-3 border-b border-nest-line shrink-0">
                    <div className="flex items-center gap-3">
                        <div className={`rounded-2xl bg-nest-clay/10 text-nest-clay grid place-items-center shrink-0 ${seniorMode ? "h-12 w-12" : "h-9 w-9 rounded-xl"}`}>
                            <Car size={seniorMode ? 24 : 18} />
                        </div>
                        <div>
                            <p className={`font-black uppercase tracking-wider text-nest-clay ${seniorMode ? "text-xs" : "nest-stat-label"}`}>
                                {isHi ? "गेट सुरक्षा खोज" : "Gate Security Search"}
                            </p>
                            <h3 className={`font-bold tracking-tight text-nest-ink ${seniorMode ? "text-xl sm:text-2xl mt-0.5" : "font-display text-lg sm:text-xl"}`}>
                                {isHi ? "वाहन नंबर प्लेट जांच" : "Vehicle Plate Lookup"}
                            </h3>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Quick Report Button */}
                        <button
                            type="button"
                            onClick={() => handleOpenReport(query)}
                            className={`rounded-2xl bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-300 font-bold transition flex items-center gap-1.5 cursor-pointer shadow-2xs ${
                                seniorMode ? "h-11 px-3.5 text-xs sm:text-sm" : "h-8 px-2.5 text-xs"
                            }`}
                            title={isHi ? "अनाधिकृत पार्किंग रिपोर्ट करें" : "Report Unauthorized Parking"}
                            data-testid="quick-report-parking-btn"
                        >
                            <BellRing size={seniorMode ? 16 : 13} />
                            <span>{isHi ? "अनाधिकृत रिपोर्ट" : "Report"}</span>
                        </button>

                        <button
                            onClick={onClose}
                            className={`grid place-items-center rounded-2xl bg-nest-alt/60 hover:bg-nest-alt text-nest-ink transition cursor-pointer ${
                                seniorMode ? "h-11 w-11" : "h-8 w-8 rounded-lg"
                            }`}
                            title={isHi ? "बंद करें" : "Close lookup"}
                        >
                            <X size={seniorMode ? 22 : 18} />
                        </button>
                    </div>
                </div>

                {/* Tabs: Search Plate vs Active Violations */}
                <div className="flex items-center gap-2 mt-3 shrink-0">
                    <button
                        type="button"
                        onClick={() => setActiveTab("search")}
                        className={`flex-1 rounded-xl font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                            seniorMode ? "h-11 text-xs sm:text-sm" : "h-9 text-xs"
                        } ${
                            activeTab === "search"
                                ? "bg-nest-ink text-white shadow-xs"
                                : "bg-nest-alt hover:bg-nest-line text-nest-ink"
                        }`}
                        data-testid="tab-search-plate"
                    >
                        <Search size={seniorMode ? 16 : 13} />
                        <span>{isHi ? "नंबर प्लेट खोज" : "Search Plate"}</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveTab("violations")}
                        className={`flex-1 rounded-xl font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                            seniorMode ? "h-11 text-xs sm:text-sm" : "h-9 text-xs"
                        } ${
                            activeTab === "violations"
                                ? "bg-rose-600 text-white shadow-xs"
                                : "bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200"
                        }`}
                        data-testid="tab-active-violations"
                    >
                        <AlertTriangle size={seniorMode ? 16 : 13} className={activeViolations.length > 0 ? "animate-pulse" : ""} />
                        <span>{isHi ? "सक्रिय अनाधिकृत" : "Active Violations"}</span>
                        {activeViolations.length > 0 && (
                            <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${
                                activeTab === "violations" ? "bg-white text-rose-700" : "bg-rose-600 text-white"
                            }`}>
                                {activeViolations.length}
                            </span>
                        )}
                    </button>
                </div>

                {activeTab === "violations" ? (
                    /* Active Violations List */
                    <div className="mt-3 flex-1 overflow-y-auto space-y-2.5 overscroll-contain pr-0.5">
                        {activeViolations.length === 0 ? (
                            <div className="py-12 text-center text-nest-ink3 space-y-2">
                                <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center mx-auto">
                                    <Check size={24} />
                                </div>
                                <p className="font-bold text-nest-ink text-sm sm:text-base">
                                    {isHi ? "कोई सक्रिय अनाधिकृत पार्किंग नहीं है" : "No Active Parking Violations"}
                                </p>
                                <p className="text-xs text-nest-ink3">
                                    {isHi ? "वर्तमान में सभी रिपोर्ट की गई समस्याओं का समाधान हो चुका है।" : "All reported parking violations have been marked resolved."}
                                </p>
                            </div>
                        ) : (
                            activeViolations.map((v) => (
                                <div
                                    key={v.alert_id}
                                    className="rounded-2xl border-2 border-rose-300 bg-rose-50/70 p-3.5 sm:p-4 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs"
                                    data-testid={`active-violation-${v.alert_id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono font-black text-sm px-2.5 py-1 rounded-xl bg-white border border-rose-200 text-rose-950 shadow-2xs">
                                                {v.vehicle_no}
                                            </span>
                                            <span className="text-[10px] font-black uppercase tracking-wider bg-rose-200 text-rose-900 px-2 py-0.5 rounded-full">
                                                Wing {v.tower}
                                            </span>
                                            {v.parking_spot && (
                                                <span className="text-xs font-semibold text-rose-800 bg-white/80 border border-rose-200 px-2 py-0.5 rounded-lg">
                                                    Spot: {v.parking_spot}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-rose-900 mt-1 font-medium">
                                            Reported by {v.reported_by || "Security Desk"} · {new Date(v.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                                        </p>
                                        {v.notes && (
                                            <p className="text-xs text-rose-800 italic mt-0.5">
                                                Notes: {v.notes}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 justify-end">
                                        {v.photo_url && (
                                            <button
                                                type="button"
                                                onClick={() => setActivePhotoVisitor({ photo_url: v.photo_url, name: `Vehicle ${v.vehicle_no}`, purpose: `Wing ${v.tower} Parking Alert` })}
                                                className="px-3 py-2 rounded-xl bg-white border border-rose-200 text-rose-900 font-bold text-xs hover:bg-rose-100 transition flex items-center gap-1.5 cursor-pointer shadow-2xs"
                                            >
                                                <Camera size={14} />
                                                <span>{isHi ? "फोटो" : "Photo"}</span>
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={resolvingId === v.alert_id}
                                            onClick={() => handleResolveAlert(v.alert_id, v.vehicle_no)}
                                            className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs transition shadow-sm flex items-center gap-1.5 cursor-pointer active:scale-95"
                                            data-testid={`resolve-btn-${v.alert_id}`}
                                        >
                                            {resolvingId === v.alert_id ? (
                                                <RefreshCw size={14} className="animate-spin" />
                                            ) : (
                                                <Check size={16} />
                                            )}
                                            <span>{isHi ? "हल हो गया / गाड़ी हट गई" : "Resolve / Vehicle Moved"}</span>
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <>
                        {/* Search Input */}
                        <div className="mt-3 shrink-0">
                    <div className="relative">
                        <Search size={seniorMode ? 22 : 16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-nest-ink3" />
                        <input
                            type="text"
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value.toUpperCase())}
                            placeholder={isHi ? "गाड़ी नंबर लिखें (उदा. MH 12, 5678)..." : "Type vehicle number (e.g. MH 12, 5678)..."}
                            className={`w-full bg-nest-alt/60 rounded-2xl border border-nest-line focus:border-nest-clay focus:bg-white focus:ring-2 focus:ring-nest-clay/20 outline-none font-mono font-black tracking-wider text-nest-ink uppercase placeholder:font-sans placeholder:normal-case placeholder:font-normal placeholder:text-nest-ink3 transition ${
                                seniorMode ? "h-14 sm:h-16 pl-12 pr-12 text-base sm:text-xl" : "py-2.5 sm:py-3 pl-10 pr-10 text-sm"
                            }`}
                            data-testid="vehicle-lookup-input"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => setQuery("")}
                                className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 text-nest-ink3 hover:text-nest-ink rounded-full hover:bg-nest-alt ${
                                    seniorMode ? "h-10 w-10 flex items-center justify-center" : ""
                                }`}
                                title={isHi ? "हटाएं" : "Clear search"}
                            >
                                <X size={seniorMode ? 20 : 14} />
                            </button>
                        )}
                    </div>
                    <p className={`mt-1.5 px-1 truncate ${seniorMode ? "text-xs font-semibold text-nest-ink2" : "text-[11px] text-nest-ink3"}`}>
                        {isHi ? "निवासी, Visitor, स्टाफ और अनाधिकृत पार्किंग का तुरंत मिलान।" : "Instant plate match across residents, passes, staff, & parking violations."}
                    </p>
                </div>

                {/* Results List */}
                <div className="mt-3 flex-1 overflow-y-auto space-y-2.5 overscroll-contain pr-0.5">
                    {loading && (
                        <div className="py-12 text-center text-nest-ink3 text-sm flex items-center justify-center gap-2">
                            <div className="h-5 w-5 rounded-full border-2 border-nest-clay border-t-transparent animate-spin" />
                            <span className={seniorMode ? "text-base font-bold text-nest-ink" : ""}>
                                {isHi ? "डेटाबेस में खोज रहे हैं…" : "Searching vehicle database…"}
                            </span>
                        </div>
                    )}

                    {!loading && !searched && query.length < 2 && (
                        <div className="py-12 text-center text-nest-ink3">
                            <div className={`rounded-full bg-nest-alt mx-auto grid place-items-center mb-2 ${seniorMode ? "h-14 w-14" : "h-12 w-12"}`}>
                                <Car size={seniorMode ? 28 : 22} className="text-nest-ink3" />
                            </div>
                            <p className={`font-semibold ${seniorMode ? "text-sm sm:text-base text-nest-ink2" : "text-xs"}`}>
                                {isHi ? "लाइसेंस प्लेट के कम से कम 2 अक्षर लिखें।" : "Type at least 2 characters of a license plate number."}
                            </p>
                        </div>
                    )}

                    {/* Vehicle Not Found -> Prompt to Mark Unauthorized & Alert Wing */}
                    {!loading && searched && results.length === 0 && (
                        <div className="py-6 text-center space-y-4 animate-fade-in">
                            <div className="text-nest-ink3">
                                <p className={`font-black text-nest-ink ${seniorMode ? "text-base sm:text-lg" : "text-sm"}`}>
                                    {isHi ? "सोसायटी डेटाबेस में गाड़ी नहीं मिली" : "Vehicle Not Found in Society Records"}
                                </p>
                                <p className={`mt-1 ${seniorMode ? "text-xs sm:text-sm text-nest-ink2" : "text-xs text-nest-ink3"}`}>
                                    {isHi ? `"${query}" से मेल खाती कोई पंजीकृत कार, बाइक या पास नहीं है।` : `No registered resident, staff, or active pass matches "${query}".`}
                                </p>
                            </div>

                            {/* Prominent Action Card to Mark Unauthorized */}
                            <div className="p-4 sm:p-5 rounded-3xl bg-rose-50 border-2 border-rose-300 text-left shadow-xs">
                                <div className="flex items-start gap-3">
                                    <div className="p-2.5 rounded-2xl bg-rose-100 text-rose-700 shrink-0">
                                        <AlertTriangle size={seniorMode ? 26 : 22} className="text-rose-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className={`font-black text-rose-950 ${seniorMode ? "text-base sm:text-lg" : "text-sm"}`}>
                                            {isHi ? "अनाधिकृत पार्किंग का संदेह?" : "Suspected Unauthorized Parking?"}
                                        </h4>
                                        <p className={`mt-0.5 text-rose-800 ${seniorMode ? "text-xs sm:text-sm" : "text-xs"}`}>
                                            {isHi
                                                ? `यदि यह गाड़ी विंग पार्किंग में गलत तरीके से खड़ी है, तो तुरंत फोटो खींचकर संबंधित विंग (उदा. विंग C) के सभी निवासियों को अलर्ट भेजें।`
                                                : `If this vehicle is parked in society parking, take a photo and alert all residents of that wing (e.g. Wing C) immediately.`}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleOpenReport(query)}
                                    className={`mt-3.5 w-full rounded-2xl bg-rose-600 hover:bg-rose-700 active:scale-[0.98] text-white font-black transition cursor-pointer shadow-lg hover:shadow-xl flex items-center justify-center gap-2.5 ${
                                        seniorMode ? "h-14 sm:h-15 text-sm sm:text-base" : "h-11 text-xs sm:text-sm"
                                    }`}
                                    data-testid="mark-unauthorized-action-btn"
                                >
                                    <BellRing size={seniorMode ? 20 : 16} />
                                    <span>{isHi ? "अनाधिकृत मार्क करें & विंग को फोटो अलर्ट भेजें" : "Mark Unauthorized & Alert Wing Residents"}</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Results Render */}
                    {!loading &&
                        results.map((item, idx) => {
                            const isUnauthorized = item.is_unauthorized;

                            return (
                                <div
                                    key={idx}
                                    className={`rounded-2xl border transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                                        isUnauthorized
                                            ? "border-rose-400 bg-rose-50/70 shadow-xs"
                                            : "border-nest-line bg-nest-alt/30 hover:bg-nest-alt/60"
                                    } ${seniorMode ? "p-4 sm:p-5" : "p-3 sm:p-3.5"}`}
                                    data-testid={`vehicle-result-${idx}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center gap-1.5 font-mono font-black rounded-xl bg-white border border-nest-line text-nest-ink shadow-2xs ${
                                                seniorMode ? "text-sm sm:text-base px-3 py-1" : "text-xs px-2 py-0.5"
                                            }`}>
                                                {item.type === "bike" ? (
                                                    <Bike size={seniorMode ? 16 : 13} className="text-nest-clay shrink-0" />
                                                ) : (
                                                    <Car size={seniorMode ? 16 : 13} className="text-nest-clay shrink-0" />
                                                )}
                                                <span>{item.vehicle_no}</span>
                                            </span>

                                            <span
                                                className={`font-black uppercase tracking-wider rounded-xl ${
                                                    seniorMode ? "text-xs sm:text-sm px-3 py-1" : "text-[9px] px-2 py-0.5"
                                                } ${
                                                    isUnauthorized
                                                        ? "bg-rose-600 text-white shadow-2xs animate-pulse"
                                                        : item.category === "Resident"
                                                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                                        : "bg-amber-100 text-amber-900 border border-amber-300"
                                                }`}
                                            >
                                                {isUnauthorized
                                                    ? (isHi ? "⚠️ अनाधिकृत पार्किंग" : "⚠️ UNAUTHORIZED")
                                                    : item.category === "Resident"
                                                    ? (isHi ? "सोसायटी निवासी" : "Resident")
                                                    : item.category === "Staff"
                                                    ? (isHi ? "स्टाफ" : "Staff")
                                                    : (isHi ? "Visitor" : item.category)}
                                            </span>
                                        </div>

                                        <p className={`font-black mt-2 truncate flex items-center gap-2 ${
                                            isUnauthorized ? "text-rose-950" : "text-nest-ink"
                                        } ${seniorMode ? "text-base sm:text-lg" : "font-semibold text-sm"}`}>
                                            {isUnauthorized ? (
                                                <AlertTriangle size={seniorMode ? 18 : 14} className="text-rose-600 shrink-0" />
                                            ) : (
                                                <User size={seniorMode ? 16 : 13} className="text-nest-clay shrink-0" />
                                            )}
                                            <span className="truncate">{item.name || (isHi ? "निवासी" : "Resident")}</span>
                                        </p>

                                        <div className="flex items-center gap-2.5 mt-1 flex-wrap">
                                            {item.flat && (
                                                <span className={`inline-flex items-center gap-1 font-bold ${
                                                    isUnauthorized
                                                        ? "text-rose-900 bg-rose-100 border border-rose-300 px-2.5 py-0.5 rounded-lg"
                                                        : seniorMode
                                                        ? "text-xs sm:text-sm text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-lg"
                                                        : "text-xs text-nest-ink2"
                                                }`}>
                                                    <Building2 size={seniorMode ? 14 : 12} className={isUnauthorized ? "text-rose-700 shrink-0" : "text-amber-700 shrink-0"} />
                                                    <span>{isUnauthorized ? `Location: ${item.flat}` : `Flat ${item.flat}`}</span>
                                                </span>
                                            )}
                                            {item.notes && (
                                                <span className="text-xs text-rose-800 italic">
                                                    · {item.notes}
                                                </span>
                                            )}
                                            {item.status && !isUnauthorized && (
                                                <span className={`uppercase font-black text-nest-ink3 ${seniorMode ? "text-xs" : "text-[10px]"}`}>
                                                    · {item.status}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 shrink-0 pt-2 sm:pt-0 border-t border-nest-line/60 sm:border-0 justify-end flex-wrap">
                                        {Boolean(item.has_photo || item.photo_url) && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setActivePhotoVisitor({
                                                        name: item.name,
                                                        phone: item.phone,
                                                        vehicle_no: item.vehicle_no,
                                                        vehicle_type: item.type,
                                                        purpose: item.category,
                                                        host_flat: item.flat,
                                                        status: item.status,
                                                        photo_url: item.photo_url,
                                                        walkin_id: item.walkin_id,
                                                        visitor_id: item.visitor_id,
                                                        has_photo: true,
                                                    })
                                                }
                                                className={`inline-flex items-center gap-1.5 font-bold rounded-xl transition shadow-2xs cursor-pointer shrink-0 ${
                                                    isUnauthorized
                                                        ? "bg-rose-100 text-rose-900 border border-rose-300 hover:bg-rose-200"
                                                        : "bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100"
                                                } ${seniorMode ? "h-11 sm:h-12 px-3.5 sm:px-4 text-xs sm:text-sm" : "px-2.5 py-1.5 text-xs"}`}
                                                title="View photo"
                                                data-testid={`lookup-photo-btn-${idx}`}
                                            >
                                                <Camera size={seniorMode ? 16 : 12} />
                                                <span>{isHi ? "फोटो" : "Photo"}</span>
                                            </button>
                                        )}

                                        {/* If this is an unauthorized parking violation, provide Resolve button */}
                                        {isUnauthorized && item.alert_id && (
                                            <button
                                                type="button"
                                                disabled={resolvingId === item.alert_id}
                                                onClick={() => handleResolveAlert(item.alert_id, item.vehicle_no)}
                                                className={`inline-flex items-center gap-1.5 font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition shadow-sm cursor-pointer shrink-0 ${
                                                    seniorMode ? "h-11 sm:h-12 px-4 text-xs sm:text-sm" : "px-3 py-1.5 text-xs"
                                                }`}
                                                title={isHi ? "गाड़ी हट गई / समस्या हल हो गई" : "Vehicle moved / Resolved"}
                                            >
                                                {resolvingId === item.alert_id ? (
                                                    <RefreshCw size={14} className="animate-spin" />
                                                ) : (
                                                    <Check size={16} />
                                                )}
                                                <span>{isHi ? "हल हो गया" : "Resolve / Moved"}</span>
                                            </button>
                                        )}

                                        {item.phone && (
                                            <>
                                                <a
                                                    href={`tel:${String(item.phone).replace(/[^0-9+]/g, "")}`}
                                                    className={`inline-flex items-center gap-1.5 font-black uppercase rounded-xl bg-nest-clay text-white hover:bg-nest-clayDark transition shadow-2xs shrink-0 ${
                                                        seniorMode ? "h-11 sm:h-12 px-4 sm:px-5 text-xs sm:text-sm" : "px-3 py-1.5 text-xs"
                                                    }`}
                                                    title={`Call ${item.phone}`}
                                                    data-testid={`call-btn-${idx}`}
                                                >
                                                    <Phone size={seniorMode ? 15 : 12} />
                                                    <span>{isHi ? "कॉल" : "Call"}</span>
                                                </a>
                                                <a
                                                    href={`https://wa.me/${String(item.phone).replace(/\D/g, "")}?text=${encodeURIComponent(
                                                        `Hello ${item.name || "Resident"}, regarding vehicle ${item.vehicle_no || ""} at the gate.`
                                                    )}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`inline-flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100 transition shadow-2xs shrink-0 ${
                                                        seniorMode ? "h-11 sm:h-12 px-3.5 sm:px-4 text-xs sm:text-sm font-black" : "px-2.5 py-1.5 text-xs font-bold"
                                                    }`}
                                                    title={`WhatsApp ${item.phone}`}
                                                    data-testid={`wa-btn-${idx}`}
                                                >
                                                    <span>WA</span>
                                                </a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </>
                )}

                {/* Footer Modal Actions */}
                <div className="pt-3 border-t border-nest-line mt-3 flex items-center justify-between shrink-0">
                    <span className={`font-bold ${seniorMode ? "text-sm text-nest-ink2" : "text-xs text-nest-ink3"}`}>
                        {results.length > 0 ? (isHi ? `${results.length} परिणाम मिले` : `${results.length} record${results.length > 1 ? "s" : ""} found`) : ""}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`rounded-2xl border border-nest-line hover:bg-nest-alt font-bold text-nest-ink transition cursor-pointer ${
                            seniorMode ? "h-11 px-6 text-sm" : "px-4 py-2 text-xs"
                        }`}
                    >
                        {isHi ? "बंद करें" : "Close"}
                    </button>
                </div>
            </div>

            {/* Photo Zoom Modal */}
            <VisitorPhotoModal visitor={activePhotoVisitor} onClose={() => setActivePhotoVisitor(null)} seniorMode={seniorMode} />

            {/* Report Unauthorized Vehicle Modal */}
            {showReportModal &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto animate-fade-in"
                        onClick={() => setShowReportModal(false)}
                    >
                        <div
                            className={`bg-white rounded-3xl border border-nest-line w-full max-w-lg ${
                                seniorMode ? "p-5 sm:p-6" : "p-4 sm:p-5"
                            } shadow-2xl my-auto animate-scale-up`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="report-unauthorized-modal"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between pb-3 border-b border-nest-line">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-2 rounded-xl bg-rose-100 text-rose-700">
                                        <AlertTriangle size={seniorMode ? 22 : 18} />
                                    </div>
                                    <div>
                                        <h3 className={`font-black text-nest-ink tracking-tight ${seniorMode ? "text-lg sm:text-xl" : "text-base"}`}>
                                            {isHi ? "अनाधिकृत वाहन पार्किंग रिपोर्ट" : "Report Unauthorized Parking"}
                                        </h3>
                                        <p className="text-[11px] font-bold text-rose-700">
                                            {isHi ? "संबंधित विंग के निवासियों को फोटो सहित अलर्ट भेजा जाएगा" : "Alert & photo will be sent to the respective wing residents"}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowReportModal(false)}
                                    className="h-8 w-8 grid place-items-center rounded-full hover:bg-nest-alt text-nest-ink transition cursor-pointer"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmitReport} className="mt-4 space-y-3.5">
                                {/* Vehicle Number */}
                                <div>
                                    <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm" : "text-xs"}`}>
                                        {isHi ? "गाड़ी नंबर (लाइसेंस प्लेट)" : "Vehicle Number (Plate)"} <span className="text-rose-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={reportVehicleNo}
                                        onChange={(e) => setReportVehicleNo(e.target.value.toUpperCase())}
                                        placeholder="e.g. MH12AB1234"
                                        className={`w-full bg-nest-alt/50 border border-nest-line rounded-xl px-3 font-mono font-black uppercase outline-none focus:border-rose-500 focus:bg-white text-nest-ink ${
                                            seniorMode ? "h-13 text-base sm:text-lg" : "h-10 text-sm"
                                        }`}
                                    />
                                </div>

                                {/* Tower / Wing Selection (e.g. Wing C) */}
                                <div>
                                    <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm" : "text-xs"}`}>
                                        {isHi ? "गाड़ी किस विंग / पार्किंग में खड़ी है?" : "Where is the vehicle parked? (Wing / Area)"} <span className="text-rose-600">*</span>
                                    </label>
                                    <select
                                        value={reportTower}
                                        onChange={(e) => setReportTower(e.target.value)}
                                        className={`w-full bg-nest-alt/50 border border-nest-line rounded-xl px-3 font-bold outline-none focus:border-rose-500 focus:bg-white text-nest-ink cursor-pointer ${
                                            seniorMode ? "h-13 text-sm sm:text-base" : "h-10 text-xs sm:text-sm"
                                        }`}
                                    >
                                        <option value="A">Wing A Parking (विंग A पार्किंग)</option>
                                        <option value="B">Wing B Parking (विंग B पार्किंग)</option>
                                        <option value="C">Wing C Parking (विंग C पार्किंग)</option>
                                        <option value="D">Wing D Parking (विंग D पार्किंग)</option>
                                        <option value="E">Wing E Parking (विंग E पार्किंग)</option>
                                        <option value="F">Wing F Parking (विंग F पार्किंग)</option>
                                    </select>
                                </div>

                                {/* Parking Spot / Bay details */}
                                <div>
                                    <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm" : "text-xs"}`}>
                                        {isHi ? "पार्किंग स्लॉट / स्थान विवरण (वैकल्पिक)" : "Parking Spot / Details (Optional)"}
                                    </label>
                                    <input
                                        type="text"
                                        value={reportSpot}
                                        onChange={(e) => setReportSpot(e.target.value)}
                                        placeholder={isHi ? "उदा. स्लॉट C-14, लिफ्ट के पास रास्ता रोक रही है" : "e.g. Slot C-14, blocking driveway near lift"}
                                        className={`w-full bg-nest-alt/50 border border-nest-line rounded-xl px-3 outline-none focus:border-rose-500 focus:bg-white text-nest-ink ${
                                            seniorMode ? "h-12 text-sm sm:text-base" : "h-10 text-xs"
                                        }`}
                                    />
                                </div>

                                {/* Photo Capture (Camera / File) */}
                                <div>
                                    <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm" : "text-xs"}`}>
                                        {isHi ? "गाड़ी का फोटो खींचें (कैमरा)" : "Capture Vehicle Photo (Camera)"}
                                    </label>

                                    {reportPhotoPreview ? (
                                        <div className="relative rounded-2xl overflow-hidden border-2 border-rose-300 bg-nest-alt inline-block shadow-sm">
                                            <img
                                                src={reportPhotoPreview}
                                                alt="Vehicle preview"
                                                className="h-36 sm:h-40 w-auto object-cover max-w-full rounded-2xl"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleClearPhoto}
                                                className="absolute top-2 right-2 p-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-md cursor-pointer transition"
                                                title="Remove photo"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label
                                            className={`flex items-center justify-center gap-2 border-2 border-dashed border-rose-400 bg-rose-50/50 hover:bg-rose-100/60 rounded-2xl transition cursor-pointer text-rose-800 font-bold ${
                                                seniorMode ? "h-20 text-sm sm:text-base" : "h-16 text-xs"
                                            }`}
                                        >
                                            <Camera size={seniorMode ? 24 : 18} className="text-rose-600" />
                                            <span>{isHi ? "कैमरा खोलें और फोटो लें" : "Open Camera & Snap Photo"}</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                onChange={handlePhotoSelect}
                                                className="hidden"
                                            />
                                        </label>
                                    )}
                                </div>

                                {/* Targeted Wing Notice Preview Callout */}
                                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3 text-xs text-amber-950">
                                    <div className="p-1.5 rounded-xl bg-amber-500/20 text-amber-800 shrink-0 mt-0.5">
                                        <Radio size={16} className="animate-pulse" />
                                    </div>
                                    <div className="space-y-0.5">
                                        <p className="font-black text-amber-900 text-xs sm:text-sm">
                                            {isHi ? `🔒 लक्षित विंग डिलीवरी:` : `🔒 Targeted Wing Delivery:`}
                                        </p>
                                        <p className="text-amber-800 leading-relaxed">
                                            {isHi
                                                ? `यह अलर्ट, फोटो और स्थान विवरण केवल विंग ${reportTower} के पंजीकृत निवासियों को भेजा जाएगा (अन्य विंग्स को परेशान नहीं किया जाएगा)।`
                                                : `This alert, photo, and spot details will be sent ONLY to registered residents of Wing ${reportTower} (other wings will not be disturbed).`}
                                        </p>
                                    </div>
                                </div>

                                {/* Submit & Cancel Action Buttons */}
                                <div className="pt-2 space-y-2.5">
                                    <button
                                        type="submit"
                                        disabled={reporting}
                                        className={`w-full rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-red-700 hover:from-red-700 hover:to-rose-800 active:scale-[0.98] text-white font-black transition-all cursor-pointer shadow-lg hover:shadow-xl border border-white/15 flex items-center justify-center gap-3 ${
                                            seniorMode ? "h-15 sm:h-16 text-base sm:text-lg" : "h-13 sm:h-14 text-sm sm:text-base"
                                        }`}
                                        data-testid="submit-unauthorized-report-btn"
                                    >
                                        {reporting ? (
                                            <>
                                                <RefreshCw size={seniorMode ? 22 : 18} className="animate-spin text-white" />
                                                <span className="tracking-wide">
                                                    {isHi ? `विंग ${reportTower} को अलर्ट भेजा जा रहा है…` : `Sending Alert to Wing ${reportTower}…`}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="grid place-items-center h-9 w-9 rounded-xl bg-white/20 text-white shadow-inner shrink-0">
                                                    <BellRing size={seniorMode ? 20 : 17} className="animate-bounce" />
                                                </span>
                                                <span className="tracking-tight">
                                                    {isHi
                                                        ? `विंग ${reportTower} के निवासियों को अलर्ट भेजें`
                                                        : `Send Alert to Wing ${reportTower} Residents`}
                                                </span>
                                            </>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setShowReportModal(false)}
                                        className={`w-full rounded-2xl border border-nest-line bg-nest-alt/60 hover:bg-nest-alt text-nest-ink font-bold transition cursor-pointer flex items-center justify-center ${
                                            seniorMode ? "h-12 text-sm sm:text-base" : "h-10 text-xs sm:text-sm"
                                        }`}
                                    >
                                        {isHi ? "रद्द करें" : "Cancel"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
